import React from "react";
import { Button, TextField, Link, Typography, Container, Card, CardContent, InputAdornment, Box } from "@mui/material";
import { EmailOutlined, LockOutlined, BusinessOutlined } from "@mui/icons-material";
import styled from "@emotion/styled";
import { useBrandName } from "../utils/brandUtils";

const StyledContainer = styled(Container)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    width: "100%",
    minHeight: "100vh",
    [theme.breakpoints.down("sm")]: {
        padding: theme.spacing(0),
    },
}));

const StyledCard = styled(Card)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(3),
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[3],
    [theme.breakpoints.up("sm")]: {
        width: "475px",
        margin: "0 auto",
    },
    [theme.breakpoints.down("sm")]: {
        padding: theme.spacing(3),
    },
}));

const StyledCardRow = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: theme.spacing(2),
    width: "100%",
    gap: theme.spacing(1),
}));

const StyledCardContent = styled(CardContent)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(3),
}));

const StyledTextField = styled(TextField, { shouldForwardProp: (prop) => prop !== "error" })(({ theme, error }) => ({
    margin: theme.spacing(2, 0),
    ...(error && {
        "& .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.error.main,
        },
        "& .MuiInputLabel-root": {
            color: theme.palette.error.main,
        },
        "& .MuiInputLabel-shrink": {
            color: theme.palette.error.main,
        },
    }),
}));

const StyledButton = styled(Button)(({ theme }) => ({
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.primary.main,
    "&:hover": {
        backgroundColor: theme.palette.primary.dark,
    },
}));

const StyledLink = styled(Link)(({ theme }) => ({
    color: theme.palette.secondary.main,
    cursor: "pointer",
    "&:hover": {
        textDecoration: "underline",
    },
}));

const StyledCopyright = styled(Typography)(({ theme }) => ({
    marginTop: theme.spacing(3),
}));

const Copyright = () => {
    const brandName = useBrandName();

    return (
        <StyledCopyright variant="body2" color="textSecondary" align="center">
            {"Copyright © "}
            <Link color="inherit" href="https://pollux-mui.com/">
                {brandName}
            </Link>{" "}
            {new Date().getFullYear()}
            {"."}
        </StyledCopyright>
    );
};

const RegisterStep1 = ({ formData, formErrors, setFormData, setFormErrors, validateField, newRegistrationStep2, stopNewRegistration }) => {

    const handleFormChange = (event) => {
        const { name, value } = event.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleBlur = (event) => {
        const { name, value } = event.target;
        const error = validateField(name, value);
        if (error) {
            setFormErrors((prev) => ({
                ...prev,
                [name]: error,
            }));
        } else {
            setFormErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    return (
        <StyledContainer component="main">
            <StyledCard>
                <StyledCardContent component="form">
                    <Typography variant="h4" component="h1" gutterBottom>
                        Register
                    </Typography>
                    <StyledCardRow>
                        <StyledTextField
                            required
                            fullWidth
                            id="first_name"
                            name="first_name"
                            label="First Name"
                            variant="outlined"
                            value={formData.first_name}
                            error={Boolean(formErrors.first_name)}
                            helperText={formErrors.first_name || ""}
                            onChange={handleFormChange}
                            onBlur={handleBlur}
                        />
                        <StyledTextField
                            required
                            fullWidth
                            id="last_name"
                            name="last_name"
                            label="Last Name"
                            variant="outlined"
                            value={formData.last_name}
                            error={Boolean(formErrors.last_name)}
                            helperText={formErrors.last_name || ""}
                            onChange={handleFormChange}
                            onBlur={handleBlur}
                        />
                    </StyledCardRow>
                    <StyledTextField
                        required
                        fullWidth
                        id="title"
                        name="title"
                        label="Job Title"
                        variant="outlined"
                        value={formData.title}
                        error={Boolean(formErrors.title)}
                        helperText={formErrors.title || ""}
                        onChange={handleFormChange}
                        onBlur={handleBlur}
                    />
                    <StyledTextField
                        required
                        fullWidth
                        id="email"
                        name="email"
                        label="Email Address"
                        type="email"
                        variant="outlined"
                        value={formData.email}
                        autoComplete="off"
                        error={Boolean(formErrors.email)}
                        helperText={formErrors.email || ""}
                        onChange={handleFormChange}
                        onBlur={handleBlur}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <EmailOutlined />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <StyledTextField
                        required
                        fullWidth
                        id="password"
                        name="password"
                        label="Password"
                        type="password"
                        variant="outlined"
                        value={formData.password}
                        autoComplete="new-password"
                        error={Boolean(formErrors.password)}
                        helperText={formErrors.password || ""}
                        onChange={handleFormChange}
                        onBlur={handleBlur}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <LockOutlined />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <StyledTextField
                        required
                        fullWidth
                        id="client"
                        name="client"
                        label="Company Name"
                        variant="outlined"
                        value={formData.client}
                        autoComplete="client"
                        error={Boolean(formErrors.client)}
                        helperText={formErrors.client || ""}
                        onChange={handleFormChange}
                        onBlur={handleBlur}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <BusinessOutlined />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <StyledButton type="button" fullWidth variant="contained" color="primary" onClick={newRegistrationStep2}>
                        Continue
                    </StyledButton>
                    <Typography variant="body2" align="center">
                        Already have an account? <StyledLink onClick={stopNewRegistration}>Log In</StyledLink>
                    </Typography>
                </StyledCardContent>
            </StyledCard>
            <Copyright />
        </StyledContainer>
    );
};

export default RegisterStep1; 